import { isDef } from '@utils/def';
import moment from 'moment';

import { ActionType, getType } from 'typesafe-actions';

import { BusinessDayConventionTypes } from '../../../../../models/BusinessDayConvention/enum';
import { DayCountTypes } from '../../../../../models/DayCountType/enums';
import { DebtLegTerms } from '../../../../../models/DebtLegTerms';
import { PayFrequencyTypes } from '../../../../../models/PayFrequency/enum';
import {
  getDefaultDiscountingIndexFromCurrencyConventions,
  getDefaultForecastingIndexFromCurrencyConventions,
  getDefaultResetCalendarIdByForecastingIndex,
} from '../../../../Pricers/store/pricer/components/MasterLegReducer/utils';

import * as pricerActions from '../../actions';
import { DebtPricerActionType } from '../../debtReducer';

import FlowsReducer, { FlowsActionType, FlowsState } from '../FlowsReducer';
import { TradeSummaryInitialState } from '../TradeSummaryReducer';

import * as flowsActions from './../FlowsReducer/actions';
import * as legsActions from './actions';
import { empty_debt_leg_terms } from './constants';

type LegsActionType = ActionType<typeof legsActions>;

export type DebtLegState = {
  leg_terms: DebtLegTerms;
  leg_flows: FlowsState;
};

export const LegInitialState: DebtLegState = {
  leg_terms: empty_debt_leg_terms,
  leg_flows: [],
};

const legReducer = (
  state: DebtLegState = LegInitialState,
  action: LegsActionType | DebtPricerActionType | FlowsActionType,
): DebtLegState => {
  switch (action.type) {
    case getType(pricerActions.buildSchedule.request):
    case getType(pricerActions.buildSchedule.failure):
      return {
        ...state,
      };
    case getType(pricerActions.buildSchedule.success): {
      return {
        ...state,
        leg_flows: action.payload.flows,
      };
    }

    case getType(legsActions.addFixedLeg): {
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.user_currency_id,
        action.payload.currency_conventions,
      );
      const maturityDate = moment(action.payload.market_date)
        .add(10, 'y')
        .format('YYYY-MM-DD');
      const calculationCalendarFiltered = action.payload.currencies.filter(
        (el) => el.currency_id == action.payload.user_currency_id,
      );
      const calculationCalendarId = calculationCalendarFiltered[0]
        ? calculationCalendarFiltered[0].default_calendar_pmca_id
        : null;
      const cds_curve_id = action.payload.companyEntities.find(
        (entity) =>
          entity.comp_entity_id === TradeSummaryInitialState.company_entity_id,
      )?.cds_curve_id;

      return {
        ...state,
        leg_terms: {
          ...empty_debt_leg_terms,
          currency_id: action.payload.user_currency_id,
          notional: 100000000,
          coupon: 1.75,
          price: 100000000,
          par: Number((100).toFixed(2)),
          debt_yield: 0.0,
          fees: 0.0,
          effective_date: action.payload.market_date,
          maturity_date: maturityDate,
          toggle_roll_fields_flag: false,
          first_payment_date: null,
          next_to_last_payment_date: null,
          eom_roll: false,
          pay_frequency: PayFrequencyTypes.quarterly,
          //pay_start_end: PaymentTypes.end,
          business_day_convention: BusinessDayConventionTypes.modifiedFollowing,
          payment_calendars: calculationCalendarId
            ? [calculationCalendarId]
            : null,
          day_count_id: DayCountTypes.act360,
          discounting_index_id: defaultIndexId,
          cds_curve_id: isDef(cds_curve_id) ? Number(cds_curve_id) : null,
          cds_spread: 0,
          collateral_currency: action.payload.user_currency_id,
          spread: 0,
          // Notional Exchange Fields
          toggle_notional_exchange_fields_flag: false,
          initial_notional_exchange: false,
          intermediary_notional_exchange: false,
          final_notional_exchange: false,
        },
        leg_flows: [],
      };
    }
    case getType(legsActions.addFloatLeg): {
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.user_currency_id,
        action.payload.currency_conventions,
      );
      const maturityDate = moment(action.payload.market_date)
        .add(10, 'y')
        .format('YYYY-MM-DD');
      const calculationCalendarFiltered = action.payload.currencies.filter(
        (el) => el.currency_id == action.payload.user_currency_id,
      );
      const calculationCalendarId = calculationCalendarFiltered[0]
        ? calculationCalendarFiltered[0].default_calendar_pmca_id
        : null;
      const defaultForecastingIndexId =
        getDefaultForecastingIndexFromCurrencyConventions(
          action.payload.user_currency_id,
          action.payload.currency_conventions,
        );
      const defaultResetCalendarId =
        getDefaultResetCalendarIdByForecastingIndex(
          defaultForecastingIndexId,
          action.payload.ir_indices,
        );
      const cds_curve_id = action.payload.companyEntities.find(
        (entity) =>
          entity.comp_entity_id === TradeSummaryInitialState.company_entity_id,
      )?.cds_curve_id;

      return {
        ...state,
        leg_terms: {
          ...empty_debt_leg_terms,
          currency_id: action.payload.user_currency_id,
          notional: 100000000,
          coupon: 1.75,
          price: 100000000,
          par: Number((100).toFixed(2)),
          debt_yield: 0,
          fees: 0,
          effective_date: action.payload.market_date,
          maturity_date: maturityDate,
          toggle_roll_fields_flag: false,
          first_payment_date: null,
          next_to_last_payment_date: null,
          eom_roll: false,
          pay_frequency: PayFrequencyTypes.quarterly,
          //pay_start_end: PaymentTypes.end,
          business_day_convention: BusinessDayConventionTypes.modifiedFollowing,
          payment_calendars: calculationCalendarId
            ? [calculationCalendarId]
            : null,
          day_count_id: DayCountTypes.act360,
          discounting_index_id: defaultIndexId,
          cds_curve_id: isDef(cds_curve_id) ? Number(cds_curve_id) : null,
          cds_spread: 0,
          collateral_currency: action.payload.user_currency_id,
          // Notional Exchange Fields
          toggle_notional_exchange_fields_flag: false,
          initial_notional_exchange: false,
          intermediary_notional_exchange: false,
          final_notional_exchange: false,
          //
          interest_rate_index_id: defaultForecastingIndexId,
          spread: 0,
          debt_floor_enabled: false, //need it?
          floor: 0,
          reset_calendars: defaultResetCalendarId
            ? [defaultResetCalendarId]
            : null,
        },
        leg_flows: [],
      };
    }

    case getType(flowsActions.updateFlowTableByCellEdit):
    case getType(flowsActions.deleteFlowRows): {
      return {
        ...state,
        leg_flows: FlowsReducer(state.leg_flows, action),
      };
    }

    case getType(pricerActions.valueTrade.success): {
      return {
        ...state,
        leg_flows: action.payload.data.flows,
      };
    }

    case getType(pricerActions.deleteLegFlows): {
      return {
        ...state,
        leg_flows: [],
      };
    }

    default:
      return state;
  }
};

export default legReducer;
